import { useMemo } from "react";
import { FormikErrors, useFormikContext } from "formik";
import { ColumnDef, SpreadsheetData } from "@components/spreadsheet/types";
import { useClasses } from "@api/class.api";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { TransactionSplitLedgerCell } from "@components/transactions/TransactionSplitV2/TransactionSplitLedgerCell";
import { SpreadsheetGrid } from "../../spreadsheet/SpreadsheetGrid";
import { CellType } from "../../spreadsheet/cells";
import { AddTransactionsModalFormikConfig } from "./AddTransactionsModal";
import styles from "./AddTransactionsModal.module.scss";

type TransactionRowErrors = Array<
    FormikErrors<AddTransactionsModalFormikConfig["transactions"][number]>
>;

export const AddTransactionsSpreadsheet = () => {
    const { values, setFieldValue, errors } =
        useFormikContext<AddTransactionsModalFormikConfig>();
    const transactions = values.transactions;

    const transactionErrors = errors.transactions as
        | TransactionRowErrors
        | undefined;
    const { enabled: isGlFirst } = useIsGlFirst();
    const entityId = values.entityId;

    const hasAnyClass = useClasses().classes.length > 0;

    const columns: ColumnDef[] = useMemo(
        () => [
            {
                field: "date",
                label: "Date",
                cellType: CellType.DATE,
                isValid: ({ row }) => !transactionErrors?.[row]?.date,
            },
            isGlFirst
                ? {
                      field: "accountId",
                      label: "Cash ledger",
                      cellType: CellType.LEDGER,
                      cellFn: (props) => (
                          <TransactionSplitLedgerCell
                              {...props}
                              value={props.value as string | null}
                              entityId={entityId}
                              canClear
                          />
                      ),
                  }
                : {
                      field: "category",
                      label: "Category",
                      cellType: CellType.CATEGORY,
                      isValid: ({ row }) => !transactionErrors?.[row]?.category,
                  },
            {
                field: "amount",
                label: "Amount",
                cellType: CellType.NUMBER,
                isMonetary: true,
                required: true,
                isValid: ({ row }) => !transactionErrors?.[row]?.amount,
            },
            {
                field: "description",
                label: "Description",
                cellType: CellType.STRING,
            },
            ...(hasAnyClass
                ? [
                      {
                          field: "classIds",
                          label: "Class",
                          cellType: CellType.CLASS,
                      },
                  ]
                : []),
        ],
        [transactionErrors, hasAnyClass, isGlFirst, entityId],
    );

    const handleChange = (newData: SpreadsheetData) => {
        setFieldValue("transactions", newData);
    };

    return (
        <SpreadsheetGrid
            noOuterBorder
            data={transactions}
            columns={columns}
            onChange={handleChange}
            gridTemplateColumns={
                hasAnyClass
                    ? "150px 1fr 150px 1fr 100px"
                    : "150px 1fr 150px 1fr"
            }
            rowClassName={styles.spreadsheetRow}
        />
    );
};
